import type { TagDefinition, TagRegistry } from './tagRegistry'

// Local copy of the FNV-1a stable hash (same as createStableTagId) so this module
// imports only TYPES from tagRegistry and avoids a value-import cycle — mirrors the
// convention in canonicalRatingTags.ts.
function stableId(seed: string): string {
  let hash = 2166136261
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return `tag_${hash.toString(36)}`
}

/**
 * Canonical "elements" (יסודות) family for The Last Human Manifesto — the author's
 * thematic taxonomy: 24 life-domain elements every section is sorted into, plus the
 * 25th (the encrypted thesis). Modeled as a tag family so assignment reuses the existing
 * section-tag mechanism (a `block`-anchored annotation on the `^` heading), with no new
 * data model. The elements board screen is a view over this subtree.
 */
export const ELEMENTS_ROOT_TAG_ID = stableId('manifesto.elements.root')

const ELEMENTS_ROOT_DISPLAY_NAME = 'יסודות'

const ELEMENTS_ROOT_TAG_DEFAULT: TagDefinition = {
  name: ELEMENTS_ROOT_DISPLAY_NAME,
  parentIds: [],
  color: '#3F7E9E',
  order: 40,
}

/** The 24 thematic elements + the encrypted thesis, in canonical order (1-based). */
const ELEMENT_SEED_NAMES: readonly string[] = [
  'הגוף',
  'הנפש',
  'התודעה והרוח',
  'תשוקה ומיניות',
  'אהבה וזוגיות',
  'הורוּת',
  'משפחת המוצא',
  'שורשים ומתים',
  'חברוּת',
  'עבודה ומקצוע',
  'כסף ורכוש',
  'כוח, מעמד והיררכיה',
  'מולדת ומקום',
  'עם וזהות',
  'פוליטיקה וצדק',
  'האחר והאויב',
  'העולם והנדודים',
  'אמת ואמונה',
  'סבל וחמלה',
  'טוב ורע, אלימות',
  'הברירה והגורל',
  'יצירה ומשמעות',
  'מי אני, ולמה עכשיו',
  'אל מי אני מדבר',
  'הטענה — הכפתור',
]

/** Stable id for the element child at the given 1-based ordinal. */
function elementChildTagId(ordinal: number): string {
  return stableId(`manifesto.elements.${ordinal}`)
}

function buildElementChildDefaults(): Record<string, TagDefinition> {
  const out: Record<string, TagDefinition> = {}
  ELEMENT_SEED_NAMES.forEach((name, i) => {
    const ordinal = i + 1
    out[elementChildTagId(ordinal)] = {
      name,
      parentIds: [ELEMENTS_ROOT_TAG_ID],
      order: ordinal,
    }
  })
  return out
}

const ELEMENT_CHILD_DEFAULTS: Readonly<Record<string, TagDefinition>> = buildElementChildDefaults()

/**
 * Ensure the elements family exists. Bootstrap-once: when the root is absent we seed the
 * root + all 25 children; once the root exists the author owns the family (we touch nothing),
 * so rename / recolor / delete / reorder all survive later loads. Child ids are stable, so
 * existing assignments stay valid even before the registry is persisted.
 */
export function ensureProtectedElementsTags(registry: TagRegistry): TagRegistry {
  if (registry.tags[ELEMENTS_ROOT_TAG_ID]) {
    return registry
  }
  return {
    ...registry,
    tags: {
      ...registry.tags,
      [ELEMENTS_ROOT_TAG_ID]: { ...ELEMENTS_ROOT_TAG_DEFAULT },
      ...ELEMENT_CHILD_DEFAULTS,
    },
  }
}
